import './ModulesLibraryDrawer.css';
import type {
  ModuleManifestEntry,
} from '../../services/host/ModulesServiceModule';
import type { ModulesLibraryState } from '../../hooks/useModulesLibrary';
import {
  getActiveScenarioId,
  moduleIdToScenarioId,
  setActiveScenarioId,
} from '../../scenarioRegistry';

/**
 * ModulesLibraryDrawer — 2026-06-07 wire-up for the seed/modules/ REST surface.
 *
 * Lists every adventure module in the manifest grouped by dialect
 * (Victory Games canon vs Fan-authored 4Eyes). Each row is a clickable
 * button that fires onSelect(module_id) — the ScenarioRunner host wires
 * the click into a detail expansion showing the loaded module JSON's
 * key sections (NPC roster, location list, mission sheet count).
 *
 * 3-state union per useModulesLibrary (mirror of usePdfLibrary): renders
 * a loading affordance when state==='loading', error message when
 * state==='error', and the grouped list when state==='ready'.
 *
 * Mirrors PdfLibraryDrawer.tsx pattern.
 */

export interface ModulesLibraryDrawerProps {
  modules: ModuleManifestEntry[];
  state: ModulesLibraryState;
  error?: string | null;
  selectedModuleId: string | null;
  selectedModuleData: Record<string, unknown> | null;
  selectedModuleState: ModulesLibraryState;
  onSelect: (moduleId: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

const DIALECT_ORDER: ReadonlyArray<{
  key: ModuleManifestEntry['dialect'];
  label: string;
}> = [
  { key: 'victory_games_canon', label: 'Victory Games canon' },
  { key: 'fan_authored_4eyes', label: 'Fan-authored (4Eyes)' },
];

function groupByDialect(
  modules: ModuleManifestEntry[],
): Map<ModuleManifestEntry['dialect'], ModuleManifestEntry[]> {
  const groups = new Map<ModuleManifestEntry['dialect'], ModuleManifestEntry[]>();
  for (const m of modules) {
    const list = groups.get(m.dialect) ?? [];
    list.push(m);
    groups.set(m.dialect, list);
  }
  return groups;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Render a summary of the selected module's key sections. */
function ModuleDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const dialect = data['dialect'] as string;
  const source = (data['source'] ?? {}) as Record<string, unknown>;

  // victory_games_canon shape
  if (dialect === 'victory_games_canon') {
    const parts = (data['parts'] ?? {}) as Record<string, unknown>;
    const npcs = (parts['part_ii_npcs'] ?? {}) as Record<string, unknown>;
    const enemies = (npcs['enemies'] ?? []) as unknown[];
    const allies = (npcs['allies'] ?? []) as unknown[];
    const places = (parts['part_iii_places_and_events'] ?? {}) as Record<
      string,
      unknown
    >;
    const locations = (places['locations'] ?? []) as unknown[];
    const missionSheets = (data['mission_sheets'] ?? []) as unknown[];

    return (
      <div
        className="br-modules-detail"
        data-testid={`${testidPrefix}-detail`}
        data-dialect={dialect}
      >
        <h3 className="br-modules-detail-title">{(data['scenario_id'] as string)}</h3>
        <dl className="br-modules-detail-stats">
          <div>
            <dt>Publisher</dt>
            <dd>{(source['publisher'] as string) || '—'}</dd>
          </div>
          <div>
            <dt>SKU</dt>
            <dd className="br-mono">{(source['sku'] as string) || '—'}</dd>
          </div>
          <div>
            <dt>Year</dt>
            <dd className="br-mono">{String(source['year'] ?? '—')}</dd>
          </div>
          <div>
            <dt>Enemies</dt>
            <dd
              className="br-mono"
              data-testid={`${testidPrefix}-detail-enemy-count`}
            >
              {enemies.length}
            </dd>
          </div>
          <div>
            <dt>Allies</dt>
            <dd
              className="br-mono"
              data-testid={`${testidPrefix}-detail-ally-count`}
            >
              {allies.length}
            </dd>
          </div>
          <div>
            <dt>Locations</dt>
            <dd
              className="br-mono"
              data-testid={`${testidPrefix}-detail-location-count`}
            >
              {locations.length}
            </dd>
          </div>
          <div>
            <dt>Mission sheets</dt>
            <dd className="br-mono">{missionSheets.length}</dd>
          </div>
        </dl>
      </div>
    );
  }

  // fan_authored_4eyes shape
  const npcsInline = (data['npcs_inline'] ?? []) as unknown[];
  const eventsSeq = (data['events_sequence'] ?? []) as unknown[];
  const endings = (data['endings'] ?? []) as unknown[];
  const handouts = (data['handouts'] ?? []) as unknown[];

  return (
    <div
      className="br-modules-detail"
      data-testid={`${testidPrefix}-detail`}
      data-dialect={dialect}
    >
      <h3 className="br-modules-detail-title">{(data['scenario_id'] as string)}</h3>
      <dl className="br-modules-detail-stats">
        <div>
          <dt>Publisher</dt>
          <dd>{(source['publisher'] as string) || '—'}</dd>
        </div>
        <div>
          <dt>Designer</dt>
          <dd>{(source['designer'] as string) || '—'}</dd>
        </div>
        <div>
          <dt>Year</dt>
          <dd className="br-mono">{String(source['year'] ?? '—')}</dd>
        </div>
        <div>
          <dt>NPCs</dt>
          <dd
            className="br-mono"
            data-testid={`${testidPrefix}-detail-npc-count`}
          >
            {npcsInline.length}
          </dd>
        </div>
        <div>
          <dt>Events</dt>
          <dd
            className="br-mono"
            data-testid={`${testidPrefix}-detail-event-count`}
          >
            {eventsSeq.length}
          </dd>
        </div>
        <div>
          <dt>Endings</dt>
          <dd className="br-mono">{endings.length}</dd>
        </div>
        <div>
          <dt>Handouts</dt>
          <dd className="br-mono">{handouts.length}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Per-module Play affordance shown inside the detail expansion. C5
 * (2026-06-07). Maps the V9 module_id to its V7 scenario_id via
 * moduleIdToScenarioId; renders three states:
 *
 * - Currently active scenario → disabled button labeled "Currently playing"
 * - V7 scenario exists for this module → enabled button that writes
 *   localStorage + reloads the page
 * - No V7 scenario for this module → disabled "Not yet authored" button
 */
function PlayScenarioButton({
  moduleId,
  displayName,
  testidPrefix,
}: {
  moduleId: string;
  displayName: string;
  testidPrefix: string;
}): JSX.Element {
  const scenarioId = moduleIdToScenarioId(moduleId);
  const activeId = getActiveScenarioId();
  if (scenarioId === null) {
    return (
      <button
        type="button"
        className="br-modules-library-play br-modules-library-play-disabled"
        data-testid={`${testidPrefix}-play-${moduleId}`}
        disabled
        title="No V7 CYOA scenario authored yet for this module."
      >
        Not yet playable — CYOA paragraphs not yet authored
      </button>
    );
  }
  if (scenarioId === activeId) {
    return (
      <button
        type="button"
        className="br-modules-library-play br-modules-library-play-active"
        data-testid={`${testidPrefix}-play-${moduleId}`}
        disabled
      >
        ▶ Currently playing — {displayName}
      </button>
    );
  }
  const handleClick = () => {
    setActiveScenarioId(scenarioId);
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };
  return (
    <button
      type="button"
      className="br-modules-library-play"
      data-testid={`${testidPrefix}-play-${moduleId}`}
      onClick={handleClick}
    >
      ▶ Play this scenario — {displayName}
    </button>
  );
}

export function ModulesLibraryDrawer({
  modules,
  state,
  error,
  selectedModuleId,
  selectedModuleData,
  selectedModuleState,
  onSelect,
  onClose,
  testidPrefix = 'br-modules-library',
}: ModulesLibraryDrawerProps) {
  const groups = groupByDialect(modules);

  return (
    <aside
      className="br-scenario-drawer br-modules-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header className="br-modules-library-header">
        <span className="br-modules-library-title">Module Library</span>
        <span
          className="br-modules-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {modules.length}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-modules-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close module library"
          >
            ✕
          </button>
        )}
      </header>

      {/* C5 (2026-06-07): V9→V7 ship. All 4 modules now have V7 CYOA
       *  scenarios authored — click any module to expand its detail +
       *  hit "Play this scenario" to switch the active adventure. The
       *  page reloads on switch so the module-level scenario const in
       *  ScenarioRunner re-initializes against the new id. */}
      <p
        className="br-modules-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Catalog of V9 reference modules + V7 CYOA scenarios. Click a module to
        expand its NPC/location/mission-sheet detail; use the
        <strong> Play this scenario</strong> button to switch the active
        adventure (the page will reload).
      </p>

      {state === 'loading' && (
        <p
          className="br-modules-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading modules…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-modules-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load modules: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && modules.length === 0 && (
        <p
          className="br-modules-library-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No modules installed in seed/modules/.
        </p>
      )}

      {state === 'ready' && modules.length > 0 && (
        <div
          className="br-modules-library-list"
          data-testid={`${testidPrefix}-list`}
        >
          {DIALECT_ORDER.map((dialectDef) => {
            const items = groups.get(dialectDef.key) ?? [];
            if (items.length === 0) return null;
            return (
              <section
                key={dialectDef.key}
                className="br-modules-library-group"
                data-dialect={dialectDef.key}
                data-testid={`${testidPrefix}-group-${dialectDef.key}`}
              >
                <h4 className="br-modules-library-group-title">
                  {dialectDef.label}
                </h4>
                <ul className="br-modules-library-rows">
                  {items.map((m) => {
                    const isSelected = m.module_id === selectedModuleId;
                    return (
                      <li
                        key={m.module_id}
                        className="br-modules-library-row-wrapper"
                      >
                        <button
                          type="button"
                          className={
                            isSelected
                              ? 'br-modules-library-row br-modules-library-row-selected'
                              : 'br-modules-library-row'
                          }
                          data-testid={`${testidPrefix}-row-${m.module_id}`}
                          data-selected={isSelected ? 'true' : 'false'}
                          onClick={() => onSelect(m.module_id)}
                          aria-pressed={isSelected}
                        >
                          <span className="br-modules-library-row-title">
                            {m.display_name}
                          </span>
                          <span className="br-modules-library-row-meta br-label">
                            {m.year} · {formatSize(m.size_bytes)}
                          </span>
                        </button>
                        {isSelected && selectedModuleState === 'loading' && (
                          <p
                            className="br-modules-library-detail-loading"
                            data-testid={`${testidPrefix}-detail-loading`}
                          >
                            Loading…
                          </p>
                        )}
                        {isSelected && selectedModuleState === 'error' && (
                          <p
                            className="br-modules-library-detail-error"
                            data-testid={`${testidPrefix}-detail-error`}
                          >
                            Could not load module.
                          </p>
                        )}
                        {isSelected &&
                          selectedModuleState === 'ready' &&
                          selectedModuleData !== null && (
                            <>
                              <ModuleDetail
                                data={selectedModuleData}
                                testidPrefix={testidPrefix}
                              />
                              <PlayScenarioButton
                                moduleId={m.module_id}
                                displayName={m.display_name}
                                testidPrefix={testidPrefix}
                              />
                            </>
                          )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}
